// The phone talk page (Safari web app), served by the relay: pair with the PC, then talk to the
// kit on it through the relay over an end-to-end encrypted leg. Ported from live-voice-ai's
// app/src/web/main.ts onto @voice-kit/ui (client, orb, karaoke transcript, page audio), with
// its iOS handling: audio unlocked and the mic prepared inside the tap, "Waking the mic…",
// recovery after a call or interruption without a reload (a fresh audio context on the next
// tap, the mic fetched again in place), a reload when a tap's mic stays silent, "tap to resume"
// hints, stop when backgrounded and reconnect on return, and the screen kept awake while talking.
import { MicSilentError, MicStartCancelled, type MicEvent } from "../capture";
import type { RebuildReason } from "../mic-recovery";
import { createPageAudio } from "../page-audio";
import { ClientError, VoiceKitClient } from "../client";
import { voiceSetup } from "./voice";
import { Orb } from "../orb";
import { dockReach, guardUnderDock } from "./dock";
import { micText, micView, orbPressed, type MicView } from "./mic-state";
import { TranscriptModel } from "../transcript";
import { mountTranscript } from "../transcript-view";
import { GuestModel } from "../guests";
import { mountGuests } from "../guests-view";
import { phoneMine } from "./mine";
import { clearCreds, codeFromHash, formatSas, loadCreds, pairWithCode, saveCreds, type Creds } from "./pairing";
import { wireUnpair } from "./unpair";
import { HeldFilter, HoldLog, HoldWatcher, returnAction, type HoldAction } from "./hold";
import { MicReconnect, resumeNeedsMic, tapAction, type MicHeld } from "./mic-reconnect";
import { clearHistory, HISTORY_MAX, historyKey, keepHistory, type History } from "./history";
import { RelayTransport, type RelayStatus } from "./relay-transport";
import { showKind } from "../transcript-view";
import { ACCEPT, AttachTray, OUTDATED_PC, prepareFile } from "../attach";
import { autoGrow, enterSends } from "../compose";
import { sendTyped, typedError } from "./typed";
import { setAudioSession } from "./audio-session";
import { loadCuesPref, saveCuesPref, SoundCues } from "./cues";
import { Sheet } from "./sheet";
import { promptHint } from "./mic-prompt";
import { CONNECT_TIMEOUT_MS, connView, orbLabel, type ConnView } from "./conn-state";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const body = document.body;
const show = (screen: "pair" | "wait" | "talk") => (body.dataset.screen = screen);
const captionEl = $("caption");
const orbHit = $("orb-hit");

const hintEl = $("hint");
const micEl = $("mic");
const micTextEl = $("mic-text");
const micLevelEl = $("mic-level");
const retryBtn = $<HTMLButtonElement>("btn-retry");
const emptyEl = $("empty");
const EMPTY = emptyEl.textContent ?? "";
const EMPTY_OFFLINE = "You can talk or type once your PC is connected.";

// Under the orb: a message when there is one (connection, errors), else what is happening
// ("Listening", "Mic off · Tap to talk", "Thinking", "Speaking", "Paused"); the mic indicator
// ("Mic on" / "Mic off", from the capture itself: ./mic-state.ts); and after a pause a hint (what
// the next tap does after a call or a stall). Temporary messages fall back to the state; the
// hint lasts until a tap.
let message = "";
let messageIsError = false;
let hint = "";
let messageTimer: ReturnType<typeof setTimeout> | null = null;
let shownMicOn = false;
let shownHeld: MicHeld = null;
function currentView(): MicView {
  const c = client;
  return micView({ kit: c?.state ?? "idle", micOn: !!c?.micCapturing, paused: !!hint, replyPaused: !!c?.paused, held: reconnect.held(c) });
}
/** Whether the page is connected to the PC (./conn-state.ts): until it is, the orb is inert. */
function currentConn(): ConnView {
  const waitedMs = connSince === null ? 0 : performance.now() - connSince;
  const view = connView({ status: sock ? connState : "connecting", everOpen, waitedMs, stuck });
  // Once said, "offline" or "can't reach" holds through the relay's redials (./conn-state.ts).
  if (view.phase === "offline" || view.phase === "failed") stuck = view.phase;
  return view;
}
function renderCaption(): void {
  const view = currentView();
  const conn = currentConn();
  const offline = conn.phase !== "open";
  const error = messageIsError && !!message;
  // Not connected: the connection's state is the line under the orb (an error still shows, for
  // its few seconds), never what a tap would do: a tap does nothing until the PC is there.
  captionEl.textContent = offline ? (error ? message : conn.label) : message || view.label;
  hintEl.textContent = hint;
  hintEl.hidden = offline || !hint || !!message || !view.label.startsWith("Paused");
  retryBtn.hidden = !conn.retry;
  emptyEl.textContent = offline ? EMPTY_OFFLINE : EMPTY;
  body.dataset.conn = conn.phase;
  body.dataset.state = error ? "error" : view.orb;
  body.dataset.mic = view.mic;
  orbHit.setAttribute("aria-pressed", String(orbPressed(view)));
  orbHit.setAttribute("aria-disabled", String(offline));
  orbHit.setAttribute("aria-label", orbLabel(conn));
  orb.setState(offline ? "idle" : error ? "error" : view.orb);
  shownMicOn = view.mic === "on";
  shownHeld = view.held ?? null;
  micEl.dataset.on = String(shownMicOn);
  micTextEl.textContent = micText(view);
  if (!shownMicOn) micLevelEl.style.setProperty("--level", "0");
}
// The capture is the source of truth and says nothing when it stops (a stall, a call): watch it
// every frame, and drive the level meter from its real loudness while the mic is on.
function watchMic(): void {
  const on = !!client?.micCapturing;
  if (on !== shownMicOn || reconnect.held(client) !== shownHeld) renderCaption();
  if (on) micLevelEl.style.setProperty("--level", capture.level().toFixed(3));
  requestAnimationFrame(watchMic);
}
function setHint(text: string): void {
  hint = text;
  renderCaption();
}
function say(text: string, ms = 0, isError = false): void {
  message = text;
  messageIsError = isError;
  if (messageTimer) clearTimeout(messageTimer);
  messageTimer = null;
  heldMs = 0;
  // Not connected, only an error shows over the connection's line: a timed note (the 12 s hint on
  // how to stop Safari asking for the mic) waits, and its time starts once it can be seen.
  if (text && ms && !isError && sock && !sock.open) heldMs = ms;
  else if (text && ms) messageTimer = setTimeout(() => say(""), ms);
  renderCaption();
}
/** A timed note's time, held while the page isn't connected (it couldn't be seen). */
let heldMs = 0;

function pref(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}
function setPref(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}

const deviceName = /iPhone/.test(navigator.userAgent) ? "iPhone" : /iPad/.test(navigator.userAgent) ? "iPad" : "Phone";
const wsUrl = location.origin.replace(/^http/, "ws") + "/client";

// One capture and one player for the page's life, on ONE shared audio context (../page-audio.ts):
// iOS routes a page's audio through one session, and a second context fought the mic for it.
// The capture recovers a mic that goes silent by itself (../mic-recovery.ts) and keeps its
// stream briefly after it stops, so a quick restart doesn't prompt again (longer in a
// home-screen web app, where iOS prompts more). The relay's CSP allows no blob: scripts, so the
// mic worklet is served as a file.
const audio = createPageAudio({ workletUrl: "capture-worklet.js" });
const { capture, player, standalone } = audio;
body.dataset.standalone = String(standalone);
// A muted mic track (another app, a brief interruption) is the mic's own state, not a call: the
// page says "Mic interrupted · Tap to reconnect" and the next tap fetches a fresh mic
// (./mic-reconnect.ts). When iOS unmutes the track by itself, it is listening again.
const reconnect = new MicReconnect(capture, (event, data) => client && reportMic(client, event, data));
// Every hold (a call, a tap, the background, a mic that needs a tap) and how it ended, for the
// PC's log: `hold_entered` / `hold_exited` with the reason and "auto" or "tap".
const holdLog = new HoldLog((event, data) => client && reportMic(client, event, data));
capture.onTrackMute = () => {
  if (client?.listening) reportMic(client, "mic_track_muted");
  renderCaption();
};
capture.onTrackUnmute = () => {
  reconnect.unmuted();
  if (client?.listening) reportMic(client, "mic_track_unmuted");
  renderCaption();
};
// What the capture does by itself, for the PC's log (logspp.log): every rebuild with its
// reason, the route and rates, and the time to the first frame, so a real device can be checked.
capture.on("*", (e: MicEvent) => {
  if (e.type === "rebuild") recovering = true;
  if (e.type === "route") lastRouteAt = performance.now();
  if (e.type === "rebuilt") {
    recovering = false;
    if (e.ok) micFailures = 0;
    renderCaption();
  }
  if (e.type === "permission_prompt") notePrompt();
  const c = client;
  if (!c || e.type === "mute" || e.type === "unmute") return; // mic_track_(un)muted above
  // The current route and rates come with every report (capture.stats().route); a route change
  // also says what it was before.
  const { type, ...extra } = e as MicEvent & Record<string, unknown>;
  if (type === "route") extra.previousRoute = extra.previous;
  delete extra.route;
  delete extra.previous;
  reportMic(c, `mic_${type}`, extra);
  if (e.type === "gesture_needed") micNeedsTap(c, e.reason);
});
// "Only listen to me": other voices' speech is never a turn. A PC that groups other voices into
// guests (1.8) gets a quiet "Guest 1 spoke" in the conversation and their words under Guests;
// an older PC shows their words dimmed and labelled, as before.
const transcript = new TranscriptModel({ showIgnored: true, guestSpeech: "marker", maxEntries: HISTORY_MAX });
let kept: History | null = null; // the conversation, kept across reloads (./history.ts)
const guests = new GuestModel();
let sock: RelayTransport | null = null;
let client: VoiceKitClient | null = null;
let connState: RelayStatus = "connecting";
/** The leg was open once since load: a drop says "Reconnecting". */
let everOpen = false;
/** Since when the page has been trying to (re)connect (performance.now()), or null while open. */
let connSince: number | null = performance.now();
let connTimer: ReturnType<typeof setTimeout> | null = null;
/** "Offline" or "can't reach", once shown: kept until the leg opens or "Try again". */
let stuck: "offline" | "failed" | null = null;
/** The relay's state changed (or "Try again", or back from the background): the orb, the caption
 * and, while not connected, the moment it gives up waiting and offers "Try again". */
function connChanged(st: RelayStatus, restart = false): void {
  const changed = st !== connState;
  connState = st;
  if (connTimer) clearTimeout(connTimer);
  connTimer = null;
  if (restart) stuck = null;
  // As before: a change of connection clears what the line said ("Paused for a call…",
  // "Resuming…", "Waking the mic…"); an error keeps its few seconds, a held note waits to be seen.
  if (changed && message && !messageIsError && !heldMs) say("");
  if (st === "open") {
    everOpen = true;
    connSince = null;
    stuck = null;
    if (heldMs && message) {
      const ms = heldMs;
      heldMs = 0;
      messageTimer = setTimeout(() => say(""), ms);
    }
  } else {
    if (connSince === null || restart) connSince = performance.now();
    const left = CONNECT_TIMEOUT_MS - (performance.now() - connSince);
    if (left > 0) connTimer = setTimeout(renderCaption, left + 50);
  }
  // "Try again" goes when the leg opens: focus moves to the orb, not lost to the page.
  const retryFocused = document.activeElement === retryBtn;
  renderCaption();
  if (retryFocused && retryBtn.hidden) orbHit.focus();
}
// Earcons (./cues.ts): heard, not seen, when the mic is really listening and when it stopped.
// Played by the page's player (the audio context unlocked in the tap), with the mic's frames
// muted to the kit while the "listening" one plays (on top of the echo canceller).
const cues = new SoundCues((kind) => player.cue(kind), (ms) => client?.muteMicFor(ms), loadCuesPref());
// "Your turn" only while this page's mic is really capturing (never after a typed turn's reply).
cues.micLive = () => client?.micCapturing ?? false;

// At rest unless the mic or the speaker is live: motion means something is happening.
const orb = new Orb($<HTMLCanvasElement>("orb"), () => client?.level() ?? 0, { restWhenIdle: true });
// "That was me" (1.10): mark ignored speech as yours; "Marked as me" under the guests.
const mine = phoneMine(() => client, $("guests-marks"), document.body, () => player.clipOutput());
mountTranscript($<HTMLOListElement>("transcript"), transcript, () => client?.progressNow() ?? null, { thatWasMe: mine.thatWasMe, clips: mine.clips, mayAct: mine.mayAct, send: mine.send });

// --- guests (1.8) -------------------------------------------------------------

const guestsSheet = $<HTMLDialogElement>("guests-sheet");
const guestsBtn = $<HTMLButtonElement>("btn-guests");
mountGuests($("guests-body"), guests, {
  rename: (id, name, save) => (client ? client.guestRename(id, name, save) : Promise.reject(new Error("Not connected"))),
  forget: (id) => (client ? client.guestForget(id) : Promise.reject(new Error("Not connected"))),
  thatWasMe: mine.thatWasMe,
  clips: mine.clips,
  mayAct: mine.mayAct,
});
function renderGuests(): void {
  const n = guests.visible.length;
  guestsBtn.hidden = !(guests.active || mine.marks.active) || (n === 0 && mine.marks.marks.length === 0);
  if (guestsSheet.open) guests.markRead();
  $("guests-badge").textContent = guests.unread ? String(guests.unread) : "";
  guestsBtn.setAttribute("aria-label", `Guests: ${n}${guests.unread ? `, ${guests.unread} new` : ""}`);
}
guests.onChange(renderGuests);
mine.marks.onChange(renderGuests);
// Guests opens half-height (drag it up for more); Settings opens tall, the page pulled back.
const guestsCtl = new Sheet(guestsSheet, { detents: ["medium", "large"] });
guestsBtn.onclick = () => {
  guests.markRead();
  guestsCtl.present();
};
$("btn-close-guests").onclick = () => void guestsCtl.dismiss();

// --- pairing ----------------------------------------------------------------

const codeInput = $<HTMLInputElement>("pair-code");
const fromLink = codeFromHash(location.hash);
if (fromLink) {
  codeInput.value = fromLink;
  history.replaceState(null, "", location.pathname); // don't leave the code in history
}

$<HTMLFormElement>("pair-form").onsubmit = async (e) => {
  e.preventDefault();
  const err = $("pair-error");
  err.textContent = "";
  try {
    const creds = await pairWithCode(codeInput.value, location.origin, deviceName);
    saveCreds(creds);
    $("sas").textContent = formatSas(creds.sas);
    show("wait");
    connect(creds);
  } catch (ex) {
    err.textContent = (ex as Error).message;
  }
};

$("btn-cancel-pair").onclick = () => forget();

function forget(): void {
  client?.close();
  client = null;
  sock = null;
  clearCreds();
  kept?.clear();
  kept = null;
  clearHistory(); // the conversation goes with the pairing
  transcript.clear();
  codeInput.value = "";
  everOpen = false; // a new pairing connects afresh
  connSince = null;
  stuck = null;
  show("pair");
}

// --- connection -------------------------------------------------------------

function connect(creds: Creds): void {
  client?.close();
  const s = new RelayTransport({ url: wsUrl, token: creds.token, c2d: creds.c2d, d2c: creds.d2c, name: deviceName });
  const c = new VoiceKitClient({ transport: s, player, capture });
  kept ??= keepHistory(transcript, historyKey(creds.token));
  sock = s;
  client = c;
  s.onRelayStatus = (st) => {
    if (client !== c) return;
    if (st === "pending") {
      $("sas").textContent = formatSas(creds.sas);
      show("wait");
      return;
    }
    if (st === "revoked") {
      forget();
      $("pair-error").textContent = "This phone was unpaired on the PC. Pair again to continue.";
      return;
    }
    if (st === "open" && !creds.confirmed) {
      creds.confirmed = true;
      saveCreds(creds);
    }
    if (st === "open" || creds.confirmed) show("talk");
    connChanged(st);
  };
  c.onAny((m) => {
    // First: it places ignored speech (this phone's mic or another's) before those lines show.
    if (client === c) mine.apply(m);
    transcript.apply(m);
    guests.apply(m);
    if (client === c && m.type === "reply.heard") cues.heard(m.outcome);
    if (m.type === "speaker.status" || m.type === "speaker.enroll.status") speakerEvent(m);
    if (m.type === "speech.ignored" && m.reason === "other_speaker") ignoredVoice();
  });
  c.on("state", () => {
    if (client === c) cues.state(c.state);
    syncOrb(c);
  });
  c.on("reply.paused", () => renderCaption());
  c.on("reply.heard", (m) => {
    // A reply paused for a call that ended another way (a new turn, the session): nothing to carry on.
    if (callPause && !c.paused) callPause = null;
    if (!c.paused && holdLog.reason === "user") holdLog.exit("ended"); // the paused reply ended another way
    if (m.outcome !== "finished") renderCaption();
  });
  // Sessions that aren't this phone's (the PC's, another phone's): a return never takes over
  // one that started, or ended, while the page was away.
  c.on("session.started", (m) => {
    if (client !== c) return;
    if (c.ownsSession) ownSid = m.session_id;
    else foreignSessions++;
  });
  c.on("session.ended", (m) => {
    if (client === c && m.session_id !== ownSid) foreignSessions++;
  });
  c.onStatus((st) => {
    if (client === c && st !== "open") legEpoch++;
  });
  c.on("session.ended", () => {
    if (client === c) reconnect.reset(); // a reconnect on its way is dropped with the session
    if (client === c && !c.listening) cues.closed();
    syncAwake();
    renderCaption();
  });
  c.on("error", (e) => {
    if (!e.re) say(e.message, 5000, true);
  });
  c.on("welcome", (m) => {
    if (client === c) {
      welcomedEpoch = legEpoch;
      if (m.session_id !== null && m.session_id !== ownSid) foreignSessions++;
    }
    void c.request({ type: "speaker.status" }).catch(() => {});
    // A PC that takes attachments says so; uploads of an earlier connection go again.
    attachBtn.hidden = !c.supports("attachments");
    tray.reconnected();
  });
  // Files that went with a spoken (or typed) turn leave the tray.
  c.on("stt.final", (m) => {
    if (m.attachments?.length) tray.sent(m.attachments.map((a) => a.id));
  });
  $("menu-device").textContent = `${deviceName} · ${formatSas(creds.sas)}`;
  void c.connect();
}

// --- talk screen ------------------------------------------------------------

/** The orb follows the kit, except that it is never "Listening" while this phone's mic isn't
 * capturing (a typed turn's session, whose reply is spoken here, the PC's, a stalled mic): it
 * says "Mic off · Tap to talk", and the next tap opens the mic. */
function syncOrb(c: VoiceKitClient): void {
  if (client !== c) return;
  if (!c.listening) cues.closed(); // the mic went (a dropped connection, a takeover)
  syncAwake();
  renderCaption();
}

// The screen stays on while the conversation is live, with voice or without: while this phone's
// voice is on, while the assistant is thinking or speaking (here or on the PC; the PC merely
// listening doesn't count), and for a while after anything new shows in the chat (a reply
// streaming in, a progress line, a push). With voice off nothing else keeps it on, and a locked
// screen closes the page's connection; what it missed comes back in `conversation.recent`.
const CHAT_AWAKE_MS = 60_000;
let chatMoving: ReturnType<typeof setTimeout> | null = null;
/** How much the chat shows (entries and their text): only growth is something new. A reconnect
 * that settles replies or merges nothing new doesn't keep the screen on. */
let chatSize = 0;
transcript.onChange(() => {
  const size = transcript.entries.reduce((n, e) => n + 1 + e.text.length, 0);
  const grew = size > chatSize;
  chatSize = size;
  if (!grew) return;
  if (chatMoving) clearTimeout(chatMoving);
  chatMoving = setTimeout(() => {
    chatMoving = null;
    syncAwake();
  }, CHAT_AWAKE_MS);
  syncAwake();
});
function syncAwake(): void {
  const c = client;
  const voiceOn = !!c && c.ownsSession && c.sessionAudio === "client";
  const working = !!c && (c.state === "thinking" || c.state === "speaking");
  void keepAwake(voiceOn || working || chatMoving !== null);
}

type WakeLock = { release(): Promise<void>; addEventListener?(type: "release", fn: () => void): void };
let wakeLock: WakeLock | null = null;
let wakeWanted = false;
let wakeBusy = false;
async function keepAwake(on: boolean): Promise<void> {
  wakeWanted = on;
  if (wakeBusy) return; // the request under way settles to the latest wish
  wakeBusy = true;
  try {
    const nav = navigator as Navigator & { wakeLock?: { request(kind: "screen"): Promise<WakeLock> } };
    while (wakeWanted !== !!wakeLock) {
      if (wakeWanted && nav.wakeLock) {
        const lock = await nav.wakeLock.request("screen");
        // The system lets it go when the page is hidden: asked for again on return.
        lock.addEventListener?.("release", () => wakeLock === lock && (wakeLock = null));
        wakeLock = lock;
      } else if (!wakeWanted && wakeLock) {
        const lock = wakeLock;
        wakeLock = null;
        await lock.release();
      } else break;
    }
  } catch {
    /* not supported, or the page is hidden: the screen may lock and Safari will pause the mic */
  } finally {
    wakeBusy = false;
  }
}

const HINT = {
  call: "Call ended. Tap to pick up where you left off.",
  callContinue: "Call ended. Tap to continue.",
  replyPaused: "Tap to continue.",
  paused: "Paused. Tap to pick up where you left off.",
  stopped: "The mic stopped. Tap once to turn it back on.",
  reloaded: "Mic restarted. Tap to pick up where you left off.",
  resume: "Tap to resume.",
};

// Mid-conversation mic stalls in a row that the capture couldn't fix by itself. Recovery
// happens in place (the capture rebuilds the mic, with backoff; then one tap, which starts
// afresh); only if that keeps failing is the page reloaded. A tap whose mic stays silent even
// after the capture's quiet retry reloads straight away.
let micFailures = 0;

/** A reply paused for a call or the background (protocol 1.9), and whether the mic was on before
 * it: the tap that carries on restores it. */
let callPause: { micWasOn: boolean } | null = null;

/** Pause the reply being spoken here: a tap, a call, the page going to the background. Never an
 * interruption: the rest is kept and the PC's work goes on. */
function pauseReply(c: VoiceKitClient, forCall: boolean): boolean {
  const wasOn = c.listening;
  if (!c.pauseReply()) return false;
  if (forCall) callPause ??= { micWasOn: wasOn };
  else holdLog.enter("user");
  renderCaption();
  return true;
}

/** Carry on the paused reply from where it stopped (inside a tap on iOS), then get the mic back
 * as it was before the pause (after a call, as before the call): a fresh one, fetched in this
 * tap, if it is off, held (its track muted) or not capturing. */
function resumeReply(c: VoiceKitClient, inTap: boolean): void {
  if (inTap) c.unlockPlayback(); // a fresh output, inside the tap, if the call left it dead
  const after = callPause;
  callPause = null;
  const micWasOn = after ? after.micWasOn : c.listening;
  c.resumeReply();
  const ours = c.ownsSession && c.sessionAudio === "client";
  const mic = { micWasOn, ours, listening: c.listening, trackMuted: capture.trackMuted(), micCapturing: c.micCapturing, held: reconnect.held(c) };
  if (inTap && resumeNeedsMic(mic)) reacquireMic(c, "resume");
  renderCaption();
}

/** Fetch the mic again inside this tap (a fresh getUserMedia track and audio context). If the
 * new track is muted too, another app has the mic: the page says so; the reply plays on. Meanwhile
 * the page says "Reconnecting the mic…" and taps leave the mic alone (./mic-reconnect.ts). */
function reacquireMic(c: VoiceKitClient, why: string): void {
  const p = reconnect.reacquire(c, why);
  renderCaption();
  p.then(
    (result) => {
      if (result === "stale") return renderCaption(); // voice ended meanwhile: nothing to do
      if (result === "ok") {
        micFailures = 0;
        cues.live();
      }
      syncOrb(c);
    },
    (e: unknown) => micStartFailed(c, e, false),
  );
}

/** Start a session (the phone's own mic and speaker), or: while speaking, pause the reply (a
 * second tap carries on; never cancels the PC's work); a held mic is reconnected (once per mute
 * episode); otherwise end the session. The decision is tapAction (./mic-reconnect.ts). */
function toggleTalk(): void {
  const c = client;
  // Not connected to the PC: the orb shows inert and a tap does nothing at all (nothing queued,
  // no mic, no audio unlocked); the caption says it is connecting.
  if (!c || !sock?.open) return renderCaption();
  const ours = c.ownsSession && c.sessionAudio === "client";
  const action = tapAction({
    replyPaused: c.paused,
    speaking: c.state === "speaking" || c.betweenChunks,
    ours,
    listening: c.listening,
    trackMuted: capture.trackMuted(),
    micStalled: capture.stalled(),
    held: reconnect.held(c),
    recovering,
    outputDead: outputDead(),
  });
  if (action === "ignore") return; // a fresh mic is on its way: a second tap would cancel it
  autoGen++; // a return's resume still on its way gives way to this tap
  if (message === RESUMING) say("");
  setHint("");
  hold.reset();
  // Whatever held the conversation (a call, the background, the user's own pause), this tap ends
  // it, and does all it takes in one go: never a tap that only re-arms the mic.
  if (action !== "pause_reply") holdLog.exit("tap", { action });
  if (action === "resume") return resumeReply(c, true);
  if (action === "rearm") {
    // The reply's output died under it (after a call) or the mic needs a tap: a fresh output in
    // this tap (the reply carries over to it, from where it was) and the mic as it was.
    setAudioSession("auto");
    c.unlockPlayback();
    const mic = { micWasOn: true, ours, listening: c.listening, trackMuted: capture.trackMuted(), micCapturing: c.micCapturing, held: reconnect.held(c) };
    if (resumeNeedsMic(mic)) return reacquireMic(c, "rearm");
    return renderCaption();
  }
  // A text-only session the phone started by typing: a tap starts talking (taking it over).
  const typing = c.ownsSession && c.sessionAudio === "none";
  if (action === "pause_reply") {
    // Only the reply pauses: the mic stays as it was, and the button says so straight away.
    if (!pauseReply(c, false)) void c.silence().catch(() => {});
    return renderCaption();
  }
  if (action === "reconnect" || action === "open_mic") {
    // The mic is held (iOS muted its track), or closed (a typed turn's session): fetch it in
    // place, inside this tap.
    setAudioSession("auto");
    c.prepareAudio();
    return reacquireMic(c, action === "reconnect" ? "tap" : "open");
  }
  if (action === "end_voice") {
    reconnect.reset();
    cues.closed();
    void c.stopSession("phone tap").catch(() => {});
    // "Voice ended": the mic goes now. The kept stream (keepStreamMs) is only for stops
    // inside a conversation (reopening the mic, a typed session switching to voice).
    capture.release();
    return;
  }
  reconnect.reset();
  // No session, or one the phone doesn't run (the PC's own, which the kit reports to every
  // client): start the phone's, which takes over. Stopping a session the phone doesn't own is
  // refused (no_session), so treating it as ours left every tap doing nothing.
  // Both must happen inside the tap on iOS, before anything is awaited.
  setAudioSession("auto");
  c.prepareAudio();
  // The session starts once the mic is really live; if that takes a moment, say so.
  const waking = "Waking the mic…";
  const slow = setTimeout(() => !c.ownsSession && say(waking), 700);
  c.startSession({ audio: "client" })
    .then(() => {
      micFailures = 0;
      if (message === waking) say("");
      if (c.listening) cues.live();
      syncOrb(c);
    })
    .catch((e: unknown) => {
      if (message === waking) say("");
      micStartFailed(c, e, typing);
    })
    .finally(() => clearTimeout(slow));
}

function micStartFailed(c: VoiceKitClient, e: unknown, typing: boolean): void {
  // A newer start (or a stop) took over: not a failure, and nothing to show.
  if (e instanceof MicStartCancelled) return void renderCaption();
  if (e instanceof MicSilentError || capture.failed) {
    // The capture's quiet retry (a fresh audio context and track) was silent too: a reload
    // is what reliably brings the mic back, so do it now rather than after another tap.
    reportMic(c, "mic_silent");
    reloadForMic();
  } else if (typing && e instanceof ClientError && e.code === "session_active") {
    // A kit that can't take a session over: end the typed one, the next tap talks.
    void c.stopSession("phone tap").catch(() => {});
    say("Tap again to talk.", 4000);
  } else if (e instanceof ClientError) say(e.message, 5000, true);
  else say(`Microphone unavailable: ${(e as Error).message}`, 6000, true);
}

/** Sends the mic's state to the PC for its log (the desktop app: logs\app.log). Best effort:
 * a kit that doesn't know `client.log` answers `unknown_type`, which is ignored. */
function reportMic(c: VoiceKitClient, event: string, extra: Record<string, unknown> = {}): void {
  const data = {
    ...extra,
    ...capture.stats(),
    ...player.stats(),
    ...audio.hub.stats(),
    micFailures,
    standalone,
    visibility: document.visibilityState,
    ua: navigator.userAgent,
  };
  void c.request({ type: "client.log", event, data }).catch(() => {});
}

/** The capture is out of automatic attempts: one tap brings the mic back (pre-warmed: the tap
 * builds a fresh context and track). A muted track that wouldn't come back keeps the session:
 * the page already says "Mic interrupted · Tap to reconnect" and the tap fetches a fresh one. */
function micNeedsTap(c: VoiceKitClient, reason: RebuildReason): void {
  if (client !== c || !c.ownsSession || c.sessionAudio !== "client" || !c.listening || hold.state !== "none") return;
  if (reason === "track_muted") {
    // iOS took the mic and the shared context with it (a call it didn't call "interrupted"): the
    // reply can't be heard either. Pause it, as a call does: the next tap re-arms and carries on.
    if ((c.state === "speaking" || c.state === "thinking") && outputDead() && pauseReply(c, true)) {
      holdLog.enter("mic");
      setHint(HINT.replyPaused);
    }
    return renderCaption();
  }
  cues.needsTap(); // the page is visible and its audio running: this one can be heard
  void c.stopSession("mic_stalled").catch(() => {});
  micFailed(HINT.stopped);
}

/** Safari asked for the mic again: counted across reloads; past a couple, say once how to stop it. */
function notePrompt(): void {
  const prompts = Number(pref("lv-mic-prompts", "0")) + 1;
  setPref("lv-mic-prompts", String(prompts));
  const text = promptHint(prompts, pref("lv-mic-prompt-hint", "") === "1", standalone);
  if (!text) return;
  setPref("lv-mic-prompt-hint", "1");
  say(text, 12_000);
}

function micFailed(text: string): void {
  capture.markStale();
  if (++micFailures >= 2) return reloadForMic();
  say("");
  setHint(text);
}

// Last resort: a fresh page reliably gets a working mic back. Pairing is kept, and so is the
// conversation on the PC; the page comes back on the talk screen saying "Tap to pick up".
function reloadForMic(): void {
  setPref("lv-mic-reloaded", String(Date.now()));
  kept?.flush();
  say("Restarting the mic…");
  setTimeout(() => location.reload(), 400);
}

// Safety net: the capture rebuilds a mic that goes silent mid-conversation by itself (a fresh
// context and track, with backoff) and says when it needs a tap (micNeedsTap). While a call
// holds the audio (MicCapture.interrupted) the page says so, and gets the mic back after it
// (./hold.ts). `recovering`: a rebuild of the capture's is on its way.
let recovering = false;
const hold = new HoldWatcher();
const PAUSED = "Paused for a call…";
// A call is a pause, not an interruption (protocol 1.9): the reply being spoken here stops where
// it is and keeps the rest (and what still arrives); when the call ends it carries on from there
// (without a tap if iOS allows it, else on the next tap), then the mic comes back as it was.
function checkHold(): void {
  const c = client;
  if (!c) return;
  const ours = c.ownsSession && c.sessionAudio === "client";
  const active = ours && (c.listening || c.state === "speaking" || c.paused);
  const visible = document.visibilityState === "visible";
  const attentive = visible && document.hasFocus();
  // Only held audio is a call; a muted mic track alone is the mic's own state (./mic-reconnect.ts).
  const interrupted = active && audioHeldNow(c);
  // A reply paused because iOS took the mic and the output (micNeedsTap): once both are back by
  // themselves (the track unmuted, the capture recovered), it carries on with no tap.
  if (holdLog.reason === "mic" && callPause && c.paused && c.micCapturing && !outputDead() && hold.state === "none") {
    holdLog.exit("auto");
    setHint("");
    carryOnAfterCall(c, "mic");
  }
  onHold(c, hold.tick(performance.now(), { active, interrupted, attentive, visible }));
}
function onHold(c: VoiceKitClient, a: HoldAction | null): void {
  if (!a) return;
  reportMic(c, "call_interrupt", { phase: a.kind === "tap" ? a.why : a.kind, replyPaused: c.paused });
  if (a.kind === "pause") {
    holdLog.enter("call");
    // A reply still open, silent or not (issue #18): its next audio must not play into the call.
    if (c.state === "speaking" || c.state === "thinking") pauseReply(c, true);
    return say(PAUSED);
  }
  if (message === PAUSED) say("");
  if (a.kind === "resume") {
    const done = (ok: boolean) => {
      if (ok) holdLog.exit("auto");
      if (ok && callPause && c.paused) carryOnAfterCall(c);
      onHold(c, hold.resumed(ok));
    };
    // The mic was off (a typed turn's reply): only the speaker needs to come back.
    if (!c.listening) return done(true);
    return void capture.resumeAfterInterruption().then(done, () => done(false));
  }
  // A tap brings it back: a fresh audio context and track, built inside that tap. A reply
  // paused for the call keeps the session: the tap carries on with it.
  if (callPause && c.paused && c.ownsSession) return setHint(HINT.callContinue);
  if (c.ownsSession) void c.stopSession("call_interrupt").catch(() => {});
  setHint(HINT.resume);
}
/** The call ended and iOS gave the audio back without a tap: carry on. If the speaker stays
 * held (iOS wants a tap after all), pause again and ask for one. */
function carryOnAfterCall(c: VoiceKitClient, reason: "call" | "background" | "mic" = "call"): void {
  const micWasOn = callPause?.micWasOn ?? false;
  resumeReply(c, false);
  setTimeout(() => {
    if (c.paused || c.state !== "speaking" || !outputDead()) return;
    pauseReply(c, true);
    holdLog.enter(reason, { after: "auto_resume" });
    if (callPause) callPause.micWasOn = micWasOn;
    setHint(HINT.callContinue);
  }, 1500);
}
setInterval(checkHold, 1000);

/** iOS holds the page's audio (a call; ./hold.ts audioHeld, HeldFilter): never a muted mic track
 * alone, and the muted-and-suspended form only once it lasts, outside a route change or rebuild. */
const heldFilter = new HeldFilter();
let lastRouteAt = -Infinity;
/** A route change or a rebuild of the audio is under way (or just was). */
function audioBusy(): boolean {
  return recovering || performance.now() - lastRouteAt < 3000 || audio.hub.msSinceBuild() < 2000;
}
function audioHeldNow(c: VoiceKitClient): boolean {
  return heldFilter.held(performance.now(), {
    speakerInterrupted: player.interrupted(),
    micOn: c.listening,
    micContextInterrupted: capture.contextInterrupted(),
    micContextSuspended: audio.hub.ctx?.state === "suspended",
    trackMuted: capture.trackMuted(),
    busy: audioBusy(),
  });
}

/** The speaker's audio context isn't running (iOS suspended or interrupted it): a reply can't be
 * heard. */
function outputDead(): boolean {
  const state = player.stats().playerCtx;
  return typeof state === "string" && state !== "running";
}

// The orb takes taps on its glass only: a round button over it (index.html), the canvas itself
// letting taps through to the conversation (phone.css). A native button: Enter, Space,
// VoiceOver and Voice Control press it like any other.
$("orb-hit").addEventListener("click", toggleTalk);
// "Try again" (the PC offline, or no answer in a while): dial now, and wait afresh.
retryBtn.addEventListener("click", () => {
  if (!sock || sock.open) return;
  sock.retry();
  connChanged(sock.relayStatus === "open" ? "open" : "connecting", true);
});

// --- typing ---------------------------------------------------------------------
// A typed message is a turn like a spoken one (./typed.ts). It never opens the mic.

const compose = $<HTMLFormElement>("compose");
const composeText = $<HTMLTextAreaElement>("compose-text");
// The box wraps and grows with the message (up to 40% of the visible page, then scrolls); the
// keyboard's Return sends, as before (../compose.ts).
const fitCompose = autoGrow(composeText);
enterSends(composeText, compose);
const composeSend = $<HTMLButtonElement>("compose-send");
const syncSend = () =>
  (composeSend.disabled = tray.busy || (!composeText.value.trim() && !tray.readyIds().length));
composeText.addEventListener("input", syncSend);
// Tapping Send keeps the keyboard up (and the layout still), as Return does.
composeSend.addEventListener("pointerdown", (e) => e.preventDefault());
compose.onsubmit = (e) => {
  e.preventDefault();
  const c = client;
  const text = composeText.value;
  const files = tray.readyIds();
  if (!c || tray.busy || (!text.trim() && !files.length)) return;
  if (!sock?.open) return say("Not connected to your PC yet.", 4000, true);
  setHint("");
  composeText.value = "";
  fitCompose();
  syncSend();
  // With the orb off (no voice session of the phone's) the reply is text only: nothing to
  // unlock. In the phone's own voice session the reply is spoken here: unlock the page's speaker
  // inside this tap, as iOS requires. A live mic keeps its audio session as it is. Typing never
  // interrupts or un-pauses the reply being spoken.
  if (c.ownsSession && c.sessionAudio === "client" && !c.listening) {
    setAudioSession("playback");
    c.unlockPlayback();
  }
  sendTyped(c, text, files).then(
    () => tray.sent(files),
    (err: unknown) => {
      if (!composeText.value) {
        composeText.value = text; // give it back to resend
        fitCompose();
      }
      syncSend();
      say(err instanceof ClientError ? typedError(err.code, err.message) : (err as Error).message, 6000, true);
    },
  );
};

// --- attachments ------------------------------------------------------------------
// A screenshot, photo or file goes with the next message, typed or spoken (./attach.ts).

const attachBtn = $<HTMLButtonElement>("compose-attach");
const attachInput = $<HTMLInputElement>("attach-input");
attachInput.accept = ACCEPT;
const tray = new AttachTray(() => (sock?.open ? client : null), prepareFile, OUTDATED_PC);
tray.onError = (text) => say(text, 6000, true);
tray.onChange = () => {
  renderTray();
  syncSend();
};
// Keep the keyboard as it is when tapping the clip (as the send button does).
attachBtn.addEventListener("pointerdown", (e) => e.preventDefault());
attachBtn.onclick = () => {
  attachInput.value = "";
  attachInput.click();
};
attachInput.onchange = () => {
  const picked = [...(attachInput.files ?? [])];
  attachInput.value = "";
  if (picked.length) tray.add(picked);
};

const trayEl = $("tray");
const trayList = $<HTMLUListElement>("tray-list");
function renderTray(): void {
  trayEl.hidden = tray.chips.length === 0;
  trayList.replaceChildren(
    ...tray.chips.map((chip) => {
      const li = document.createElement("li");
      li.className = `chip ${chip.state}`;
      if (chip.thumb) {
        const img = document.createElement("img");
        img.src = chip.thumb;
        img.alt = "";
        li.append(img);
      } else {
        const kind = document.createElement("span");
        kind.className = "kind";
        showKind(kind, chip.mime, chip.name);
        li.append(kind);
      }
      const name = document.createElement("span");
      name.className = "name";
      name.textContent = chip.name;
      li.title = chip.error ?? chip.name;
      li.append(name);
      if (chip.state === "uploading" || chip.state === "preparing") {
        const bar = document.createElement("progress");
        bar.max = 1;
        if (chip.state === "uploading") bar.value = chip.progress;
        li.append(bar);
      }
      const x = document.createElement("button");
      x.type = "button";
      x.className = "remove";
      x.setAttribute("aria-label", `Remove ${chip.name}`);
      x.textContent = "\u00d7";
      x.addEventListener("pointerdown", (ev) => ev.preventDefault());
      x.onclick = () => tray.remove(chip.id);
      li.append(x);
      return li;
    }),
  );
}

// iOS doesn't shrink the page for the keyboard: it pans the page up under the keyboard (by
// scrolling the window or the visual viewport), which would leave the header up behind the
// status bar and notch. While typing, the page (a fixed body, phone.css) is pinned to the
// visible area instead: its top and height follow the visual viewport, so the header stays
// under the status bar and the box sits just above the keyboard. When the keyboard goes,
// any pan iOS leaves behind is undone (html is overflow:hidden, so nobody could scroll it back).
const viewport = window.visualViewport;
const rootStyle = document.documentElement.style;
function fitToKeyboard(): void {
  if (!viewport) return;
  if (body.dataset.typing !== "true") {
    if (window.scrollY || window.scrollX) window.scrollTo(0, 0);
    return;
  }
  rootStyle.setProperty("--app-h", `${Math.round(viewport.height)}px`);
  rootStyle.setProperty("--app-top", `${Math.round(Math.max(0, viewport.offsetTop))}px`);
  body.dataset.cramped = viewport.height < 380 ? "true" : "false"; // landscape: drop the orb
}
viewport?.addEventListener("resize", fitToKeyboard);
viewport?.addEventListener("scroll", fitToKeyboard);
composeText.addEventListener("focus", () => {
  body.dataset.typing = "true";
  fitToKeyboard();
});
composeText.addEventListener("blur", () => {
  body.dataset.typing = "false";
  body.dataset.cramped = "false";
  rootStyle.removeProperty("--app-h");
  rootStyle.removeProperty("--app-top");
  window.scrollTo(0, 0);
});

// --- large title, scroll edges and the dock --------------------------------------
// The large title (the app's name) scrolls with the conversation and hands over to the small one in the
// bar once it has gone under it; the scroll edge blurs only appear once something is beneath.
// The conversation is padded clear of the floating dock, whose height follows what it holds.

const scroller = $("scroller");
const dock = $("dock");
let scrollQueued = false;
function syncScroll(): void {
  scrollQueued = false;
  const top = scroller.scrollTop;
  const collapsed = top > 30;
  if ((body.dataset.collapsed === "true") !== collapsed) body.dataset.collapsed = String(collapsed);
  const under = scroller.scrollHeight - top - scroller.clientHeight > 4;
  if ((body.dataset.under === "true") !== under) body.dataset.under = String(under);
}
scroller.addEventListener(
  "scroll",
  () => {
    if (!scrollQueued) (scrollQueued = true), requestAnimationFrame(syncScroll);
  },
  { passive: true },
);
const talkScreen = $("screen-talk");
const orbWrap = $("orb-wrap");
// Controls that scrolled under the dock take no taps (./dock.ts).
// Also on the visual viewport's resize and scroll: the keyboard changing height (QuickType,
// emoji) moves the dock on iOS with nothing else firing.
const coverUnderDock = guardUnderDock(
  scroller,
  $("transcript"),
  () => dock.getBoundingClientRect().bottom - dockReach(dock.offsetHeight, orbWrap.offsetTop),
  viewport ?? null,
);
const syncDock = () => {
  // The orb hangs over the dock's top: the conversation keeps clear of that too.
  talkScreen.style.setProperty("--dock-h", `${dockReach(dock.offsetHeight, orbWrap.offsetTop)}px`);
  syncScroll();
  coverUnderDock();
};
if (typeof ResizeObserver === "function") new ResizeObserver(syncDock).observe(dock);
syncDock();

// --- menu ---------------------------------------------------------------------

const menu = $<HTMLDialogElement>("menu");
const menuCtl = new Sheet(menu, { detents: ["large"] });
const CONN_TEXT: Record<RelayStatus, string> = {
  connecting: "Connecting…",
  open: "Connected to your PC",
  pending: "Waiting for confirmation",
  "pc-offline": "PC offline",
  revoked: "Unpaired",
  closed: "Disconnected",
};
$("btn-menu").onclick = () => {
  $("menu-conn").textContent = CONN_TEXT[connState];
  void client?.request({ type: "speaker.status" }).catch(() => {});
  menuCtl.present();
};
$("btn-close-menu").onclick = () => void menuCtl.dismiss();
const unpair = wireUnpair(
  {
    ask: $<HTMLButtonElement>("btn-forget"),
    confirm: $("unpair-confirm"),
    cancel: $<HTMLButtonElement>("btn-unpair-cancel"),
    yes: $<HTMLButtonElement>("btn-unpair-yes"),
  },
  () => {
    menu.close();
    forget();
  },
);
menu.addEventListener("close", () => unpair.reset());

// --- "only listen to me" (PLAN.md section 6): one voice profile, learned on each mic, kept on the PC ---

let speakerAvailable = false;
let verifyOn = false;
/** The user's voice was learned on the PC's mic / on this phone's mic. */
let learnedOnPc = false;
let learnedHere = false;
let enrollAction = "Set up my voice";
let hintShown = false;

function speakerEvent(m: { type: string } & Record<string, any>): void {
  if (m.type === "speaker.status") {
    speakerAvailable = !!m.available;
    verifyOn = !!m.enabled;
    const setup = voiceSetup(m.profiles ?? []);
    learnedOnPc = setup.pc;
    learnedHere = setup.phone;
    enrollAction = setup.action;
    renderSpeaker();
    return;
  }
  // speaker.enroll.status
  const panel = $("enroll");
  $<HTMLProgressElement>("enroll-progress").value = m.progress ?? 0;
  if (m.prompt) $("enroll-prompt").textContent = m.prompt;
  $("enroll-message").textContent = m.message ?? "";
  if (m.state === "recording" || m.state === "processing") {
    panel.hidden = false;
    body.dataset.enrolling = "true";
    if (m.state === "processing") $("enroll-message").textContent = "Learning your voice…";
    return;
  }
  panel.hidden = true;
  body.dataset.enrolling = "false";
  const done: Record<string, string> = {
    saved: learnedOnPc ? "Got it. Your voice now works from this phone too." : "Got it. The PC now knows your voice.",
    cancelled: "Voice setup cancelled.",
    failed: m.message ? `Voice setup failed: ${m.message}` : "Voice setup failed.",
  };
  say(done[m.state] ?? "", 5000, m.state === "failed");
}

function renderSpeaker(): void {
  const verify = $<HTMLButtonElement>("btn-verify");
  const enroll = $<HTMLButtonElement>("btn-enroll");
  verify.disabled = enroll.disabled = !speakerAvailable;
  verify.setAttribute("aria-checked", String(verifyOn));
  $("verify-state").textContent = verifyOn ? "On" : "Off";
  $("enroll-label").textContent = enrollAction;
  $("voice-pc").textContent = learnedOnPc ? "Learned" : "Not yet";
  $("voice-phone").textContent = learnedHere ? "Learned" : "Not yet";
  $("voice-mics").hidden = !speakerAvailable || !(learnedOnPc || learnedHere);
  $("speaker-foot").textContent = speakerAvailable
    ? "Your voice is one profile, learned on each mic: a phone sounds different from the PC, so read the sentences once here too. Other people nearby are ignored. Your voiceprint stays on the PC."
    : "Not available on this PC yet.";
}

/** "Not your voice" while this phone's mic was never learned: say how to fix it, once. */
function ignoredVoice(): void {
  if (hintShown || learnedHere || !learnedOnPc) return;
  hintShown = true;
  say("Your voice was learned on the PC, not this phone. Settings > Add this phone to my voice.", 7000, true);
}
renderSpeaker();

function renderCues(): void {
  $("btn-cues").setAttribute("aria-checked", String(cues.enabled));
  $("cues-state").textContent = cues.enabled ? "On" : "Off";
}
renderCues();
$("btn-cues").onclick = () => {
  cues.enabled = !cues.enabled;
  saveCuesPref(cues.enabled);
  renderCues();
  // Turned on: a quiet sample of what it sounds like (this tap unlocks the page's audio), unless
  // the mic is open or a reply is playing.
  if (cues.enabled && !client?.listening && client?.state !== "speaking") {
    player.unlock();
    player.cue("turn");
  }
};

$("btn-verify").onclick = () => {
  void client?.setSpeakerVerification(!verifyOn).catch((e: ClientError) => say(e.message, 5000, true));
};

$("btn-enroll").onclick = () => {
  const c = client;
  if (!c) return;
  void menuCtl.dismiss();
  // Enrollment reads the session's mic: start one first if needed (still inside this tap).
  const begin = () => {
    cues.live();
    return c.enrollStart("device").catch((e: ClientError) => say(e.message, 5000, true));
  };
  if (!c.ownsSession || !c.listening) setAudioSession("auto");
  if (!c.ownsSession) {
    c.prepareAudio();
    c.startSession({ audio: "client" }).then(begin, (e: Error) => say(e.message, 5000, true));
  } else if (!c.listening && c.sessionAudio === "client") {
    // A typed session's mic is closed: open it first.
    c.prepareAudio();
    c.listen().then(begin, (e: Error) => say(e.message, 5000, true));
  } else void begin();
};
$("btn-enroll-done").onclick = () => void client?.enrollStop(true).catch(() => {});
$("btn-enroll-cancel").onclick = () => void client?.enrollStop(false).catch(() => {});

// --- background and return ------------------------------------------------------

// iOS suspends pages in the background (a phone call, switching apps): stop listening, reconnect
// on return. The PC keeps the conversation and its work. Back within AUTO_RESUME_MAX_MS with no
// call, where the mic comes back with no prompt (a Safari tab, permission granted), it carries on
// by itself; otherwise the next tap carries on, re-arming the mic in the same tap.
let resumeHint = "";
/** The page went to the background with the phone's voice on: on return within
 * AUTO_RESUME_MAX_MS, with no call and a mic that comes back with no prompt, it carries on with
 * no tap (./hold.ts returnAction); otherwise one tap re-arms and carries on. */
let away: { at: number; voiceWasOn: boolean; userPaused: boolean; call: boolean; kept: boolean; foreign: number } | null = null;
/** This phone's last session, and how many sessions not its own started or ended since load. */
let ownSid: number | null = null;
let foreignSessions = 0;
/** Bumped when the relay leg drops; set to it by the next `welcome` (the kit's state is known). */
let legEpoch = 0;
let welcomedEpoch = 0;
/** Bumped by a tap: a return's resume still on its way gives way. */
let autoGen = 0;
const RESUMING = "Resuming…";
/** The page was hidden since it last came back (returned() also runs on a mere focus). */
let wasHidden = false;
function backgrounded(): void {
  wasHidden = true;
  capture.markStale();
  const c = client;
  if (!c || !c.ownsSession) return;
  // Only held audio is a call (./hold.ts): a muted mic track alone isn't.
  const call = hold.state !== "none" || heldFilter.last || player.interrupted() || (c.listening && capture.contextInterrupted());
  if (!away) {
    const ours = c.sessionAudio === "client";
    const userPaused = c.paused && !callPause;
    const voiceWasOn = ours && (c.listening || c.state === "speaking" || c.state === "thinking" || c.paused);
    away = { at: Date.now(), voiceWasOn, userPaused, call, kept: false, foreign: foreignSessions };
    if (voiceWasOn && !userPaused) holdLog.enter(call ? "call" : "background");
  } else away.call ||= call;
  // A reply being spoken here (an incoming call takes the screen): pause it, keep the session,
  // and carry on with the next tap. If the connection drops meanwhile, the rest is unspoken.
  if (c.sessionAudio === "client" && (c.paused || ((c.state === "speaking" || c.state === "thinking") && pauseReply(c, true)))) {
    callPause ??= { micWasOn: false };
    if (away) away.kept = true; // the session goes on: the return carries on the reply
    resumeHint = call ? HINT.callContinue : HINT.replyPaused;
    return;
  }
  resumeHint = call ? HINT.call : HINT.paused;
  cues.closed(); // nothing is heard while hidden (the player won't play), just the state
  void c.stopSession("backgrounded").catch(() => {});
}
function returned(): void {
  sock?.wake();
  // A long time away isn't a slow connection: the wait for "Can't reach your PC" starts again.
  if (wasHidden && document.visibilityState === "visible") {
    wasHidden = false;
    if (sock && !sock.open) connChanged(connState, true);
  }
  const a = document.visibilityState === "visible" ? away : null;
  if (a) away = null;
  const c = client;
  if (a && c) {
    const hiddenMs = Date.now() - a.at;
    const call = a.call || player.interrupted() || capture.contextInterrupted() || hold.state !== "none";
    const act = returnAction({ hiddenMs, voiceWasOn: a.voiceWasOn, userPaused: a.userPaused, call, quiet: capture.canStartQuietly() });
    reportMic(c, "return", { hiddenMs, action: act.kind, why: act.kind === "tap" ? act.why : null });
    if (act.kind === "auto") {
      void autoResume(c, a.kept, a.foreign, resumeHint);
      resumeHint = "";
    }
  }
  if (resumeHint) setHint(resumeHint);
  resumeHint = "";
  checkHold();
  syncAwake();
}

/** Back from a short trip to the background with no call: the mic again (no prompt: only where
 * getUserMedia can't show one) and the conversation carries on, with no tap. If iOS refuses,
 * the hint is shown and one tap does it all. Never reloads, never prompts. */
async function autoResume(c: VoiceKitClient, kept: boolean, foreign: number, fallback: string): Promise<void> {
  const gen = ++autoGen;
  const stale = () => gen !== autoGen || client !== c;
  say(RESUMING);
  try {
    // The leg may be waking (then the kit's state is known from its `welcome`); a session stopped
    // for the background must have ended first.
    await until(() => !!sock?.open && welcomedEpoch === legEpoch && (kept || !c.ownsSession), 5000);
    if (stale()) return;
    // Another device's voice started (or ended) while the phone was away, or one runs now: the
    // phone stays idle, its mic closed. Only a tap takes the conversation back.
    if (foreignSessions !== foreign || (c.sessionId !== null && !c.ownsSession)) {
      if (message === RESUMING) say("");
      reportMic(c, "return_resume_skipped", { why: "taken_over" });
      holdLog.exit("ended", { why: "taken_over" });
      return syncOrb(c);
    }
    if (kept) {
      if (!c.ownsSession || c.sessionAudio !== "client") throw new Error("session_gone");
      // The session was kept (a reply was paused for the background): the mic as it was, then the reply.
      const micWasOn = callPause?.micWasOn ?? c.listening;
      if (micWasOn && (!c.listening || capture.trackMuted() || !c.micCapturing)) {
        const r = await reconnect.reacquire(c, "auto_return");
        if (stale() || r === "stale") return;
        if (r !== "ok") throw new Error(r);
      }
      if (c.paused && callPause) carryOnAfterCall(c, "background");
    } else {
      await c.startSession({ audio: "client" });
      if (stale()) return;
    }
    if (message === RESUMING) say("");
    micFailures = 0;
    holdLog.exit("auto");
    if (c.listening) cues.live();
    syncOrb(c);
  } catch (e) {
    if (stale()) return;
    if (message === RESUMING) say("");
    reportMic(c, "return_resume_failed", { error: (e as Error)?.message ?? String(e) });
    // One tap brings it all back (a fresh context and track, built inside that tap).
    setHint(fallback || HINT.paused);
  }
}

/** Resolves once `ok()` holds (polled), or rejects after `ms`. */
function until(ok: () => boolean, ms: number): Promise<void> {
  const t0 = Date.now();
  return new Promise((resolve, reject) => {
    const check = () => {
      if (ok()) return resolve();
      if (Date.now() - t0 >= ms) return reject(new Error("not_ready"));
      setTimeout(check, 100);
    };
    check();
  });
}
document.addEventListener("visibilitychange", () => (document.visibilityState === "hidden" ? backgrounded() : returned()));
window.addEventListener("pagehide", backgrounded);
window.addEventListener("pageshow", returned);
window.addEventListener("focus", returned);
window.addEventListener("online", () => sock?.wake());

// --- start ------------------------------------------------------------------

if (Date.now() - Number(pref("lv-mic-reloaded", "0")) < 15000) {
  setPref("lv-mic-reloaded", "0");
  setHint(HINT.reloaded);
}

// Always show a screen straight away: never wait on the connection to draw something.
const saved = loadCreds();
if (saved && !fromLink) {
  $("sas").textContent = formatSas(saved.sas);
  show(saved.confirmed ? "talk" : "wait");
  connect(saved);
} else {
  show("pair");
}
renderCaption();
requestAnimationFrame(watchMic);
